import { Box, Tab as MuiTab, Tabs as MuiTabs } from '@mui/material'
import {
  Children,
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import SwipeableViews from 'react-swipeable-views'

import { TabPanel } from './TabPanel'

export type TabsProps = {
  labels: string[]
  value: number
  hiddenLabel?: boolean
  disableAnimateTransitions?: boolean
  onChange: (value: number) => void
  children: ReactNode[]
}

export const Tabs: FC<TabsProps> = ({
  labels,
  value,
  onChange,
  hiddenLabel,
  disableAnimateTransitions,
  children,
}) => {
  const width = useScreenWidth()
  const handleChange = useCallback(
    (event: SyntheticEvent, newValue: number) => {
      onChange(newValue)
    },
    [onChange]
  )

  return (
    <Box>
      {!hiddenLabel && (
        <MuiTabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons={width > 600}
          sx={{
            pb: 4,
            '& .MuiTabs-flexContainer': {
              borderBottomWidth: 1,
              borderBottomStyle: 'solid',
              borderBottomColor: 'grey.100',
              width: 'fit-content',
              minWidth: '100%',
            },
          }}
        >
          {labels.map((label, index) => (
            <MuiTab
              label={label}
              key={index}
              {...a11yProps(index)}
              sx={{
                whiteSpace: 'nowrap',
                minWidth: `calc(100% / ${labels.length})`,
              }}
            />
          ))}
        </MuiTabs>
      )}
      <SwipeableViews
        index={value}
        onChangeIndex={onChange}
        disabled={hiddenLabel}
        animateTransitions={!(hiddenLabel || disableAnimateTransitions)}
      >
        {Children.map(children, (child, index) => (
          <TabPanel index={index} value={value} key={index}>
            {child}
          </TabPanel>
        ))}
      </SwipeableViews>
    </Box>
  )
}

function a11yProps(index: number) {
  return {
    id: `utoniq-tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  }
}

// 画面幅の取得
const useScreenWidth = () => {
  const getInnerWidth = () => window?.innerWidth
  const [width, setWidth] = useState(getInnerWidth())

  useEffect(() => {
    if (!window) return

    const onResize = () => {
      setWidth(getInnerWidth())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return width
}
